import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle, IconSearch } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import useFetch from '../../helpers/useFetch';
import Spinner from '../Spinner';

const CONFLICT_LABELS = {
  'duplicate-waid': 'Already imported',
  'duplicate-name': 'A local template already uses this name',
  'invalid-payload': 'Template has no body content',
  'carousel-not-supported': 'Carousel templates cannot be imported yet. Recreate the template in Releva instead.',
  'no-available-_releva-name': 'No free _releva name (10 attempted)',
  'media-header-with-button':
    'Templates with both an image/video/document header and a button URL cannot be auto-copied — Meta does not return the original media. Recreate this template manually in Releva.',
};

const WhatsappImportModal = ({ opened, onClose, onImported }) => {
  const { accountId, domainId } = useParams();
  const fetch = useFetch();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('');
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!opened) {
      setSelected(new Set());
      setFilter('');
      setError(null);
      setTemplates([]);
      return;
    }
    let ignore = false;
    setLoading(true);
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/whatsappMessages/importable`)
      .then(({ templates }) => {
        if (!ignore) setTemplates(templates || []);
      })
      .catch((e) => {
        if (!ignore) setError(e?.message || 'Unable to load templates from Meta.');
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [opened, accountId, domainId, fetch]);

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return templates;
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(needle) ||
        (t.proposedName || '').toLowerCase().includes(needle) ||
        (t.preview?.body || '').toLowerCase().includes(needle)
    );
  }, [templates, filter]);

  const importableInFiltered = useMemo(() => filtered.filter((t) => !t.conflict), [filtered]);
  const allFilteredSelected =
    importableInFiltered.length > 0 && importableInFiltered.every((t) => selected.has(t.waid));

  const toggleOne = (waid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(waid)) {
        next.delete(waid);
      } else {
        next.add(waid);
      }
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        importableInFiltered.forEach((t) => next.delete(t.waid));
      } else {
        importableInFiltered.forEach((t) => next.add(t.waid));
      }
      return next;
    });
  };

  const handleImport = () => {
    const waids = Array.from(selected);
    if (waids.length === 0) return;
    setImporting(true);
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/whatsappMessages/import`, {
      method: 'POST',
      body: JSON.stringify({ waids }),
    })
      .then((response) => {
        const importedCount = response.imported?.length || 0;
        const skippedCount = response.skipped?.length || 0;
        const failedCount = response.failed?.length || 0;
        const copyCount = (response.imported || []).filter((i) => i.kind === 'copy').length;

        const messageParts = [`Imported ${importedCount}`];
        if (copyCount > 0) {
          messageParts.push(`${copyCount} as _releva copies (PENDING Meta review for click tracking)`);
        }
        if (skippedCount > 0) messageParts.push(`${skippedCount} skipped`);
        if (failedCount > 0) messageParts.push(`${failedCount} failed`);

        notifications.show({
          title: failedCount > 0 ? 'Import partially completed' : 'Import completed',
          message: messageParts.join(' · '),
          color: failedCount > 0 ? 'yellow' : 'success',
        });
        if (typeof onImported === 'function') {
          onImported(response);
        }
        onClose();
      })
      .catch((e) => {
        notifications.show({
          title: 'Import failed',
          message: e?.message || 'Unable to import templates.',
          color: 'red',
        });
      })
      .finally(() => setImporting(false));
  };

  const renderRow = (t) => {
    const disabled = Boolean(t.conflict);
    const isSelected = selected.has(t.waid);
    const conflictLabel = t.conflict ? CONFLICT_LABELS[t.conflict] || t.conflict : null;
    const bodyPreview = (t.preview?.body || '').slice(0, 80);

    return (
      <Table.Tr key={t.waid} c={disabled ? 'dimmed' : undefined}>
        <Table.Td>
          <Checkbox
            checked={isSelected}
            disabled={disabled}
            onChange={() => toggleOne(t.waid)}
            aria-label={`Select ${t.name}`}
          />
        </Table.Td>
        <Table.Td>
          <Stack gap={2}>
            <Text size="sm" fw={500}>
              {t.name}
            </Text>
            {t.kind === 'copy' && !disabled && (
              <Text size="xs" c="dimmed">
                → {t.proposedName}
              </Text>
            )}
            {conflictLabel ? (
              <Text size="xs" c="red">
                {conflictLabel}
              </Text>
            ) : (
              bodyPreview && (
                <Text size="xs" c="dimmed" lineClamp={2}>
                  {bodyPreview}
                  {(t.preview?.body || '').length > 80 ? '…' : ''}
                </Text>
              )
            )}
          </Stack>
        </Table.Td>
        <Table.Td>
          <Badge size="sm" variant="light">
            {t.language}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Badge size="sm" variant="light" color={t.category === 'AUTHENTICATION' ? 'blue' : 'gray'}>
            {t.category}
          </Badge>
        </Table.Td>
        <Table.Td>
          {t.status && (
            <Badge size="sm" color={t.status === 'APPROVED' ? 'green' : 'yellow'}>
              {t.status}
            </Badge>
          )}
        </Table.Td>
        <Table.Td>
          {t.kind === 'copy' ? (
            <Tooltip label="Has a button URL — Releva will create a fresh copy in Meta with our click-tracking URL. The original Meta template stays untouched.">
              <Badge size="sm" color="grape" leftSection={<IconInfoCircle size={12} />}>
                Copy
              </Badge>
            </Tooltip>
          ) : (
            <Badge size="sm" color="teal">
              Direct
            </Badge>
          )}
        </Table.Td>
      </Table.Tr>
    );
  };

  const selectedCount = selected.size;
  const importableCount = templates.filter((t) => !t.conflict).length;

  return (
    <Modal
      opened={opened}
      onClose={importing ? () => {} : onClose}
      closeOnClickOutside={!importing}
      closeOnEscape={!importing}
      withCloseButton={!importing}
      title="Import WhatsApp templates from Meta"
      size="full"
    >
      <Stack>
        <Alert color="blue" icon={<IconInfoCircle size={16} />}>
          <Stack gap="xs">
            <Text size="sm">
              Templates without a button URL are imported directly with their existing Meta status. Templates with a
              button URL are created as <Text span fw={500}>{`<name>_releva`}</Text> copies so button clicks are routed
              through Releva and show up in your stats — these will be PENDING Meta review until approved. Your original
              Meta templates are never modified.
            </Text>
            <Text size="sm">
              Variables in imported templates are renamed to <Text span fw={500}>{`{{var1}}, {{var2}}, ...`}</Text> (and{' '}
              <Text span fw={500}>{`{{var_h1}}`}</Text> for header variables). Edit each template after import to rename
              them to real Releva merge tags like <Text span fw={500}>{`{{firstName}}`}</Text> before sending.
            </Text>
          </Stack>
        </Alert>

        {error && <Alert color="red">{error}</Alert>}

        {loading ? (
          <Spinner />
        ) : templates.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No templates found in your WhatsApp Business Account.
          </Text>
        ) : (
          <>
            <Group justify="space-between" wrap="wrap">
              <TextInput
                placeholder="Filter by name or body"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                leftSection={<IconSearch size={14} />}
                style={{ flex: 1, minWidth: 200 }}
              />
              <Text size="sm" c="dimmed">
                {selectedCount} selected of {importableCount} importable ({templates.length} total)
              </Text>
            </Group>

            <ScrollArea.Autosize mah={420}>
              <Table verticalSpacing="sm" striped highlightOnHover stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 40 }}>
                      <Checkbox
                        checked={allFilteredSelected}
                        indeterminate={!allFilteredSelected && importableInFiltered.some((t) => selected.has(t.waid))}
                        disabled={importableInFiltered.length === 0}
                        onChange={toggleAllFiltered}
                        aria-label="Select all importable"
                      />
                    </Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Lang</Table.Th>
                    <Table.Th>Category</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Mode</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filtered.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={6}>
                        <Box ta="center" py="md">
                          <Text c="dimmed" size="sm">
                            No templates match the filter.
                          </Text>
                        </Box>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    filtered.map(renderRow)
                  )}
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>
          </>
        )}

        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose} disabled={importing}>
            Cancel
          </Button>
          <Button onClick={handleImport} loading={importing} disabled={selectedCount === 0 || loading}>
            {selectedCount > 0 ? `Import ${selectedCount} selected` : 'Import'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default WhatsappImportModal;
